import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { createPaginationContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

import { cutoffs } from '../../constants';

import Tiles from '../Tiles/Tiles';

import actions from '../../actions/FavoritesActions';
import get from 'lodash.get';

function ItemsPaginationWrapper(props) {
    const portfolioNode = get(props, 'portfolioNode') || {};
    const portfolioItems = portfolioNode.items || {};
    const totalResults = portfolioItems.totalResults;
    const {
        setPartialPageLoading,
        setTotalResults,
        relay: { hasMore, loadMore, isLoading },
        reordering,
        increaseNumItems,
    } = props;

    useEffect(() => {
        function loadMoreItems() {
            if (hasMore()) {
                setPartialPageLoading(true);
                loadMore(cutoffs.ADDITIONAL_ITEMS, () => {
                    setPartialPageLoading(false);
                    increaseNumItems(cutoffs.ADDITIONAL_ITEMS);
                });
            }
        }

        function handleScroll() {
            const positionY = window.scrollY || window.pageYOffset;
            if (reordering) {
                window.requestAnimationFrame(() => {
                    const scrollPosition = window.innerHeight + positionY;
                    const halfDocumentHeight = document.body.offsetHeight * 0.5;

                    const threshold = scrollPosition >= halfDocumentHeight;

                    if (threshold && hasMore() && !isLoading()) {
                        loadMoreItems();
                    }
                });
            }
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            setPartialPageLoading(false);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasMore, isLoading, loadMore, setPartialPageLoading, reordering, increaseNumItems]);

    useEffect(() => {
        if (totalResults >= 0) {
            setTotalResults(totalResults);
        }
    }, [totalResults, setTotalResults]);

    if (!totalResults) {
        return null;
    }

    return (
        <Tiles
            addNote={props.addNote}
            currency={props.currency}
            onDrop={props.onDrop}
            isPublic={props.isPublic}
            portfolioItemsConnection={portfolioItems}
            reordered={props.reordered}
            showProjects={props.showProjects}
            type={props.type}
            user={props.user}
            viewer={props.viewer || null}
            viewMode={props.viewMode}
        />
    );
}

ItemsPaginationWrapper.propTypes = {
    first: PropTypes.number,
    reordering: PropTypes.bool,
    isPublic: PropTypes.bool,
    addNote: PropTypes.func,
    currency: PropTypes.string,
    onDrop: PropTypes.func,
    portfolioNode: PropTypes.object,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    searchTerm: PropTypes.string,
    increaseNumItems: PropTypes.func,
    setPartialPageLoading: PropTypes.func,
    setTotalResults: PropTypes.func,
    showSeller: PropTypes.bool,
    showProjects: PropTypes.bool,
    sortBy: PropTypes.string,
    type: PropTypes.string,
    user: PropTypes.object,
    viewMode: PropTypes.string,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        increaseNumItems: items => {
            dispatch(actions.increaseNumItems({ items }));
        },
        setPartialPageLoading: isLoadingPartialPage => {
            dispatch(actions.setPartialPageLoading({ isLoadingPartialPage }));
        },
        setTotalResults: totalResults => {
            dispatch(actions.setTotalResults({ totalResults }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemsPaginationWrapper);

ItemsPaginationWrapper = createPaginationContainer(
    ReduxWrapped,
    {
        portfolioNode: graphql`
            fragment ItemsPaginationWrapper_portfolioNode on PortfolioV2 {
                id
                items(first: $first, after: $after, searchTerm: $searchTerm, sortBy: $sortBy)
                    @connection(key: "ItemsRefetchWrapper_items") {
                    totalResults
                    numberOfPages
                    pageInfo {
                        hasNextPage
                        endCursor
                    }
                    edges {
                        cursor
                        node {
                            item {
                                serviceId
                                vertical
                                seller {
                                    serviceId
                                }
                            }
                        }
                    }
                    ...Tiles_portfolioItemsConnection
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.portfolioNode && props.portfolioNode.items;
        },
        getFragmentVariables(prevVars, first) {
            return {
                ...prevVars,
                first,
            };
        },
        getVariables(props, { count, cursor }) {
            return {
                first: count,
                after: cursor,
                nodeId: props.portfolioNode.id,
                searchTerm: props.searchTerm,
                sortBy: props.sortBy,
                userId: props.user.serviceId,
                hasUserId: !!props.user.serviceId,
                showSeller: props.showSeller,
                isTrade: props.user.isVerifiedTrade,
            };
        },
        query: graphql`
            query ItemsPaginationWrapperQuery(
                $after: String
                $first: Int!
                $nodeId: ID!
                $searchTerm: String
                $showSeller: Boolean!
                $sortBy: PortfolioItemsSortByTypes
                $userId: String!
                $hasUserId: Boolean!
                $isTrade: Boolean!
            ) {
                node(id: $nodeId) {
                    ...ItemsPaginationWrapper_portfolioNode
                }
            }
        `,
    }
);

export default ItemsPaginationWrapper;
